// Module Rapprochement bancaire — confrontation manuelle des écritures du
// journal Banque avec le relevé bancaire réel. Pointage manuel, calcul d'écart
// en temps réel, validation immuable (verrouillage des mouvements pointés).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  CheckCircle2, AlertTriangle, Lock, Loader2, ScrollText, ArrowDownLeft, ArrowUpRight,
} from "lucide-react";

const MOIS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const anneeCourante = new Date().getFullYear();
const fmt = (n) => Math.round(n || 0).toLocaleString("fr-FR");

export default function RapprochementBanque() {
  const { company } = useAuth();
  const [exercices, setExercices] = useState([]);
  const [exercice, setExercice] = useState(String(anneeCourante));
  const [mois, setMois] = useState(String(new Date().getMonth() + 1));
  const [data, setData] = useState(null);
  const [soldeReleve, setSoldeReleve] = useState(0);
  const [pointes, setPointes] = useState(new Set());
  const [chargement, setChargement] = useState(true);
  const [enregistre, setEnregistre] = useState(false);
  const hydratant = useRef(true);

  useEffect(() => {
    api.get("/exercices").then(({ data }) => {
      setExercices(data || []);
      if (data && data.length) setExercice(String(data[0].annee));
    }).catch(() => {});
  }, []);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get("/rapprochements/banque", {
        params: { exercice: parseInt(exercice, 10), mois: parseInt(mois, 10) },
      });
      hydratant.current = true;
      setData(data);
      setSoldeReleve(data.solde_releve || 0);
      setPointes(new Set(data.ecritures_pointees || []));
    } catch {
      setData(null);
    } finally {
      setChargement(false);
    }
  }, [exercice, mois]);

  useEffect(() => { charger(); }, [charger]);

  const verrouille = data?.statut === "validé";

  const soldePointe = useMemo(() => {
    if (!data) return 0;
    let t = data.solde_initial || 0;
    data.mouvements.forEach((m) => {
      if (pointes.has(m.id)) t += m.sens === "entree" ? m.montant : -m.montant;
    });
    return Math.round(t);
  }, [data, pointes]);

  const ecart = Math.round((Number(soldeReleve) || 0) - soldePointe);

  // Sauvegarde automatique du brouillon (solde relevé + pointages)
  useEffect(() => {
    if (hydratant.current) { hydratant.current = false; return; }
    if (verrouille) return;
    const t = setTimeout(async () => {
      try {
        await api.put("/rapprochements/banque", {
          exercice: parseInt(exercice, 10), mois: parseInt(mois, 10),
          solde_releve: Number(soldeReleve) || 0, ecritures_pointees: [...pointes],
        });
        setEnregistre(true);
        setTimeout(() => setEnregistre(false), 1500);
      } catch {
        toast.error("La sauvegarde a échoué.");
      }
    }, 600);
    return () => clearTimeout(t);
  }, [soldeReleve, pointes, verrouille, exercice, mois]);

  const togglePointe = (id) => {
    if (verrouille) return;
    setPointes((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const valider = async () => {
    try {
      const { data: maj } = await api.post("/rapprochements/banque/valider", {
        exercice: parseInt(exercice, 10), mois: parseInt(mois, 10),
      });
      hydratant.current = true;
      setData(maj);
      setSoldeReleve(maj.solde_releve);
      setPointes(new Set(maj.ecritures_pointees || []));
      toast.success("Rapprochement validé et verrouillé.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La validation a échoué.");
    }
  };

  return (
    <div className="fenua-fade-up" data-testid="page-rapprochement-banque">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
          <ScrollText className="h-6 w-6 text-[#1D3D3D]" />
        </div>
        <div>
          <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">Rapprochement bancaire</h1>
          <p className="text-sm text-[#1D3D3D]/70">Pointez vos écritures Banque avec votre relevé réel.</p>
        </div>
      </div>

      {/* Étape 1 + 2 : période et solde relevé */}
      <div className="mt-6 bg-white border border-[#4A8A8A]/20 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Exercice</Label>
          <Select value={exercice} onValueChange={setExercice}>
            <SelectTrigger data-testid="filtre-exercice" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(exercices.length ? exercices : [{ id: "x", annee: anneeCourante }]).map((e) => (
                <SelectItem key={e.id} value={String(e.annee)}>Exercice {e.annee}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Mois</Label>
          <Select value={mois} onValueChange={setMois}>
            <SelectTrigger data-testid="filtre-mois" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MOIS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Solde de mon relevé bancaire (XPF)</Label>
          <Input type="number" data-testid="champ-solde-releve" value={soldeReleve} disabled={verrouille}
            onChange={(e) => setSoldeReleve(e.target.value === "" ? 0 : Math.round(Number(e.target.value) || 0))}
            className="h-9 text-right tabular-nums bg-white border-[#4A8A8A]/30 disabled:opacity-70" />
        </div>
      </div>

      {/* Bandeau de verrouillage */}
      {verrouille && (
        <div data-testid="bandeau-valide" className="mt-4 rounded-lg px-4 py-3 flex items-center gap-3 bg-[#E8F4F4] border border-[#4A8A8A]/30 text-[#1D3D3D] text-sm font-semibold">
          <Lock className="h-5 w-5 text-[#2D5A5A]" />
          Rapprochement validé le {data.valide_le?.slice(0, 10)} — verrouillé, non modifiable.
        </div>
      )}

      {/* Alerte : mois précédent non rapproché */}
      {data?.precedent_non_rapproche && (
        <div data-testid="alerte-precedent-non-rapproche" className="mt-4 rounded-lg px-4 py-3 flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <span>
            <strong>{data.precedent_non_rapproche.libelle}</strong> n'est pas encore rapproché
            ({data.precedent_non_rapproche.nb_mouvements} mouvement{data.precedent_non_rapproche.nb_mouvements > 1 ? "s" : ""} bancaire{data.precedent_non_rapproche.nb_mouvements > 1 ? "s" : ""}).
            Pour des soldes fiables, rapprochez d'abord le mois précédent.
          </span>
        </div>
      )}

      {chargement ? (
        <div className="mt-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
      ) : !data ? (
        <p className="mt-6 text-center text-sm text-[#1D3D3D]/60">Aucune donnée pour cette période.</p>
      ) : (
        <>
          {/* Étape 3 : écritures à pointer */}
          <div className="mt-6 bg-white border border-[#4A8A8A]/20 rounded-lg overflow-hidden">
            <div className="bg-[#2D5A5A] text-white px-4 py-2 text-sm font-semibold flex items-center justify-between">
              <span>Écritures du journal Banque — {data.periode_libelle}</span>
              {enregistre && <span className="text-xs font-normal flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Enregistré</span>}
            </div>
            {data.mouvements.length === 0 ? (
              <p data-testid="aucun-mouvement" className="p-8 text-center text-sm text-[#1D3D3D]/60">Aucune écriture bancaire à rapprocher sur cette période.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#E8F4F4] text-[#1D3D3D] text-left">
                    <th className="px-3 py-2 w-10"></th>
                    <th className="px-3 py-2 font-semibold">Date</th>
                    <th className="px-3 py-2 font-semibold">Libellé</th>
                    <th className="px-3 py-2 font-semibold">Tiers / catégorie</th>
                    <th className="px-3 py-2 font-semibold text-right">Entrée</th>
                    <th className="px-3 py-2 font-semibold text-right">Sortie</th>
                    <th className="px-3 py-2 font-semibold">Pièce</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#4A8A8A]/10">
                  {data.mouvements.map((m) => {
                    const coche = pointes.has(m.id);
                    return (
                      <tr key={m.id} data-testid={`ligne-${m.id}`}
                        className={`${coche ? "bg-[#E8F4F4]/50" : "hover:bg-[#E8F4F4]/30"} cursor-pointer`}
                        onClick={() => togglePointe(m.id)}>
                        <td className="px-3 py-2">
                          <input type="checkbox" data-testid={`pointer-${m.id}`} checked={coche} disabled={verrouille}
                            onChange={() => togglePointe(m.id)} onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 accent-[#2D5A5A] cursor-pointer" />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-[#1D3D3D]/80">{m.date_mouvement}</td>
                        <td className="px-3 py-2 text-[#1D3D3D]">{m.description}</td>
                        <td className="px-3 py-2 text-[#1D3D3D]/70">{m.contrepartie_libelle || "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-[#2D5A5A] font-medium">{m.sens === "entree" ? fmt(m.montant) : "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-[#8A4A4A] font-medium">{m.sens === "sortie" ? fmt(m.montant) : "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs text-[#4A8A8A]">{m.reference || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Étape 4 : bandeau de contrôle */}
          <div data-testid="bandeau-controle" className={`mt-4 rounded-lg p-5 ${ecart === 0 ? "bg-[#E8F4F4] border border-[#2D5A5A]/30" : "bg-amber-50 border border-amber-200"}`}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center sm:text-left">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#4A8A8A]">Solde relevé bancaire</p>
                <p data-testid="solde-releve" className="text-lg font-bold text-[#1D3D3D] tabular-nums">{fmt(soldeReleve)} XPF</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#4A8A8A]">Solde pointé Porinetia Compta</p>
                <p data-testid="solde-pointe" className="text-lg font-bold text-[#1D3D3D] tabular-nums">{fmt(soldePointe)} XPF</p>
                <p className="text-xs text-[#1D3D3D]/50">dont solde initial {fmt(data.solde_initial)} XPF</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#4A8A8A]">Écart</p>
                <p data-testid="ecart" className={`text-lg font-black tabular-nums ${ecart === 0 ? "text-[#2D5A5A]" : "text-amber-700"}`}>{fmt(ecart)} XPF</p>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-sm font-semibold">
              {ecart === 0 ? (
                <><CheckCircle2 className="h-5 w-5 text-[#2D5A5A]" /><span className="text-[#1D3D3D]">Rapprochement équilibré — vous pouvez valider.</span></>
              ) : (
                <><AlertTriangle className="h-5 w-5 text-amber-600" /><span className="text-amber-800">Écart de {fmt(Math.abs(ecart))} XPF — vérifiez votre relevé ou vos saisies.</span></>
              )}
            </div>
          </div>

          {/* Étape 5 : validation */}
          {!verrouille && (
            <div className="mt-4 flex justify-end">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="bouton-valider" disabled={ecart !== 0 || data.mouvements.length === 0}
                    className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white disabled:opacity-50">
                    <Lock className="h-4 w-4 mr-1" /> Valider le rapprochement
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="dialog-validation">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Valider définitivement le rapprochement ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Ce rapprochement ({data.periode_libelle}) sera <strong>définitivement verrouillé</strong> et
                      ne pourra plus être modifié. Les écritures pointées passeront en statut « rapproché ». Confirmer ?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="annuler-validation">Annuler</AlertDialogCancel>
                    <AlertDialogAction data-testid="confirmer-validation" onClick={valider} className="bg-[#1D3D3D] hover:bg-[#2D5A5A]">
                      Valider et verrouiller
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </>
      )}
    </div>
  );
}
